/* global google */
import React, { useState, useEffect, useRef } from "react";
import "./AutoCompleteLocation.scss";

const NewLocationAutocomplete = ({
  label,
  onPlaceSelected,
  value,
  required = false,
  disabled = false,
  region = "ca",
  language = "en",
}) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef();
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initSession = () => {
    if (!sessionTokenRef.current && window.google?.maps?.places) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }
  };

  const handleInput = async (e) => {
    const inputVal = e.target.value;
    setQuery(inputVal);

    if (!inputVal.trim()) {
      setSuggestions([]);
      onPlaceSelected({ label: "", lat: "", lng: "" });
      return;
    }

    try {
      if (!window.google || !window.google.maps) {
        console.warn("Google Maps API not yet loaded.");
        return;
      }

      initSession();

      const { places } = await google.maps.importLibrary("places");

      //fetchAutocompleteSuggestions is new and stable in v=weekly
      const { suggestions: fetchedSuggestions } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: inputVal,
          region,
          language,
          sessionToken: sessionTokenRef.current,
        });

      const mapped = fetchedSuggestions.map((s) => ({
        placeId: s.placePrediction.placeId,
        query: s.placePrediction.text?.text || "",
      }));

      setSuggestions(mapped);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      setSuggestions([]);
    }
  };

  const handleSelect = async (suggestion) => {
    if (!suggestion.placeId) return;

    try {
      const { places } = await google.maps.importLibrary("places");
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ placeId: suggestion.placeId }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const formatted = results[0].formatted_address;

          setQuery(formatted);
          onPlaceSelected({
            label: formatted,
            lat: location.lat(),
            lng: location.lng(),
          });

          setSuggestions([]);
          sessionTokenRef.current = null;
        }
      });
    } catch (err) {
      console.error("Error resolving place details:", err);
    }
  };

  return (
    <div className="autocomplete-wrapper" ref={inputRef}>
      <input
        type="text"
        className="common-input"
        value={query}
        onChange={handleInput}
        onFocus={initSession}
        disabled={disabled}
        placeholder={label || "Enter a location"}
        required={required}
        autoComplete="off"
      />

      {query.trim() && suggestions.length > 0 && (
        <div className="autocomplete-dropdown">
          {suggestions.map((sug) => (
            <div
              key={sug.placeId}
              className="autocomplete-option"
              onClick={() => handleSelect(sug)}
            >
              {sug.query}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewLocationAutocomplete;
